<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <section :class="$style.sectionRoot">
        <header :class="$style.sectionHeader">
          Workday Authorization
        </header>
        <div :class="$style.sectionContent">
          <p>
            You can connect JumpCloud to your Workday Tenant to import user data to make user
            provisioning easier.
          </p>
          <FlatActionButton
            :action="handleAuthorizeButtonClick"
            :buttonClass="authorizeButtonClass"
          >
            {{ authorizeButtonText }}
          </FlatActionButton>
        </div>
      </section>
      <section :class="$style.sectionRoot">
        <header :class="$style.sectionHeader">
          Workday Import
        </header>
        <div :class="$style.sectionContent">
          <InputValidator
            :config="{
              attribute: 'reportUrl',
              dataLpignore: 'true',
              disabled: !isAuthorized,
              label: 'Custom Report URL',
              name: 'Custom Report URL',
              validate: 'required|reportUrlValidation',
              value: reportUrl,
            }"
            :tooltip="reportUrlTooltip"
            @InputValidatorModel="handleReportUrlChange"
          />
          <p :class="$style.rowSpacing">
            <strong>Last Import Date:</strong>
            <span data-test-id="WorkdayDetails__lastImportDate">{{ formattedLastInputDate }}</span>
          </p>
          <FlatActionButton
            :action="handleImportWorkdayUsersClick"
            :disabled="importButtonDisabled"
          >
            Import Workday Users
          </FlatActionButton>
          <p :class="$style.rowSpacing">
            An import from Workday can be run at any time. The import will populate new
            user data based on your
            <a
              :href="workdayKbCreateReportUrl"
              rel="noopener noreferrer"
              target="_blank"
            >
              Custom Report.
            </a>
          </p>
        </div>
      </section>
    </template>
  </DetailsAsideLayout>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import ConfirmationModal from 'client/components/Modal/ModalComponents/ConfirmationModal.vue';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import WorkdayBasicAuthModal from 'client/directories/Workday/WorkdayBasicAuthModal.vue';
import WorkdayImport from 'client/directories/Workday/WorkdayImport.vue';
import moment from 'moment';

const {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;
const { settings, workday } = ItemTypeConsts;
const settingsModule = StoreModuleMap[settings];
const workdayModel = StoreModelMap[workday];

export default {
  name: 'WorkdayDetails',

  inject: ['$validator'],

  components: {
    DetailsAsideLayout,
    FlatActionButton,
    InputValidator,
  },

  computed: {
    ...mapState(workdayModel, {
      lastImportDate: state => state.item.lastImportDate,
      reportUrl: state => state.item.reportUrl,
      workdayId: state => state.item.id,
    }),

    ...mapGetters(workdayModel, [
      'isAuthorized',
    ]),

    ...mapGetters(settingsModule, {
      workdayKbUrl: 'getWorkdayKBUrl',
      workdayKbCreateReportUrl: 'getWorkdayKBCreateReportUrl',
    }),

    authorizeButtonClass() {
      const classes = ['btnDefault'];

      if (this.isAuthorized) {
        classes.push('btnAlert');
      }

      return classes;
    },

    authorizeButtonText() {
      return `${this.isAuthorized ? 'De-authorize' : 'Authorize'} Workday`;
    },

    formattedLastInputDate() {
      if (this.lastImportDate) {
        return moment(this.lastImportDate).format(Constants.default.dateTimeLongFormat);
      }

      return Constants.default.dataNotAvailable;
    },

    importButtonDisabled() {
      if (this.isAuthorized && this.reportUrl && !this.hasErrors()) {
        return false;
      }

      return true;
    },

    reportUrlTooltip() {
      return {
        text: 'Read our Knowledge Base article for instructions to obtain the report URL.',
        kbLink: this.workdayKbUrl,
        kbLinkText: 'Visit the Workday Knowledge Base',
      };
    },
  },

  beforeMount() {
    this.$validator.extend('reportUrlValidation', {
      getMessage: 'Incorrect custom Workday report URL.',
      validate: this.$options.methods.validateReportUrl,
    });
  },

  methods: {
    ...mapActions(workdayModel, [
      'deauthorizeWorkday',
      'saveItem',
      'updateItem',
    ]),

    handleAuthorizeButtonClick() {
      if (this.isAuthorized) {
        ModalEventBus.open({
          component: ConfirmationModal,
          componentProps: {
            bodyText: 'You are de-authorize the connection to your Workday Tenant. Once completed you will no longer be able to import user data from your Workday Custom Report. Do you wish to proceed?',
            continueText: 'Yes',
            handleContinue: () => this.handleDeauthorizeWorkday(),
            headerText: 'Deauthorize Workday',
            mainActionAttention: true,
          },
        });
      } else {
        ModalEventBus.open({ component: WorkdayBasicAuthModal });
      }
    },

    async handleDeauthorizeWorkday() {
      const { error, success } = Constants.default;

      try {
        await this.deauthorizeWorkday();
        notify(success, 'Workday has been de-authorized.');
      } catch (e) {
        notify(error, 'Unable to de-authorize Workday!');
      }

      ModalEventBus.close();
    },

    handleImportWorkdayUsersClick() {
      ModalEventBus.open({
        component: WorkdayImport,
        componentProps: {
          workdayId: this.workdayId,
        },
      });

      EventHelper.triggerDirectoryImportUsersLinkClicked({ resourceName: 'workday' });
    },

    async handleReportUrlChange(value, key) {
      await this.$validator.validateAll();

      if (!this.hasErrors()) {
        this.updateItem({ [key]: value });
        this.saveItem();
      }
    },

    hasErrors() {
      return this.v2Errors.any();
    },

    validateReportUrl(value = '') {
      const REPORT_URL_REGEXP = new RegExp(/^https:\/\/.*workday.*/i);
      const REPORT_MOCK_URL_REGEXP = new RegExp(/^http:\/\/workday-reportmock.*/i);

      if (value.match(REPORT_URL_REGEXP) || value.match(REPORT_MOCK_URL_REGEXP)) {
        return true;
      }

      return false;
    },
  },
};
</script>
<style module>
@import '../../css/sections.css';

/*
 * TODO: Are there common styles that can be used for this?
 * SEE: https://github.com/TheJumpCloud/jumpcloud-admin-portal/pull/1252#discussion_r339256495;
 */
.rowSpacing {
  padding-top: 1.33em;
}
</style>
